"use client";

import {useRef} from "react";
import {Dialog, OverlayArrow, Popover} from "react-aria-components";
import {NavDropdownListProps} from "@/app/_components/layout/MobileMenu/MenuTypes";
import Link from "next/link";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronDown} from "@fortawesome/sharp-regular-svg-icons/faChevronDown";
import clsMerge from "@/app/util/clsMerge";
import {
	navElementClasses,
	navDropdownClasses,
	navElementWrapperClasses,
} from "@/app/_components/layout/navElementClasses";
import {FCWithChildren} from "@/app/types/reactTypes";
import Triangle from "@/public/triangle.svg";

interface NavDropdownItemWithChildrenProps
	extends Omit<NavDropdownListProps, "menuItems"> {
	popoverOffset?: number;
}

const NavDropdownItemWithChildren: FCWithChildren<
	NavDropdownItemWithChildrenProps
> = ({
	id,
	label,
	href,
	children,
	classes,
	hasChevron = true,
	isOpen,
	popoverOffset = 14,
	onMouseEnter,
	onMouseLeave,
	onFocus,
	onBlur,
}) => {
	const menuTriggerRef = useRef<HTMLDivElement | null>(null);

	/**
	 * Per the WAI menubar guidelines (https://www.w3.org/WAI/ARIA/apg/patterns/menubar/)
	 * a submenu should be a sibling of the parent menuitem
	 */
	const popoverContainerRef = useRef<HTMLDivElement | null>(null);
	const headingLabel = (
		<>
			<span className={classes?.label}>{label}</span>
			{hasChevron && (
				<FontAwesomeIcon
					className={classes?.chevron}
					icon={faChevronDown}
					size="sm"
				/>
			)}
		</>
	);

	return (
		<>
			<div
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				className={clsMerge(navElementWrapperClasses, "flex", classes?.root)}
				aria-haspopup={true}
				aria-expanded={isOpen}
				role="menuitem"
			>
				<div
					ref={menuTriggerRef}
					className={clsMerge(
						navElementClasses,
						"flex h-full items-center gap-2",
						classes?.labelRoot
					)}
				>
					{href ? (
						<Link href={href} id={id} onFocus={onFocus} onBlur={onBlur}>
							{headingLabel}
						</Link>
					) : (
						headingLabel
					)}
				</div>
				<Popover
					isOpen={isOpen}
					triggerRef={menuTriggerRef}
					isNonModal
					containerPadding={0}
					offset={popoverOffset}
					className={clsMerge(
						"group flex flex-col items-start shadow-[0_3px_5px_rgba(0,0,0,0.05)]",
						classes?.popover
					)}
					placement="bottom start"
					UNSTABLE_portalContainer={popoverContainerRef?.current || undefined}
				>
					<OverlayArrow
						className={clsMerge(
							"-translate-x-1/2 rotate-180 fill-white stroke-white",
							classes?.overlayArrow
						)}
						style={{transform: ""}}
					>
						<Triangle />
					</OverlayArrow>
					<Dialog
						className={clsMerge(navDropdownClasses, classes?.dialog)}
						aria-label={`${typeof label === "string" ? label : id} Submenu`}
					>
						{children}
					</Dialog>
				</Popover>
			</div>
			<div
				ref={popoverContainerRef}
				aria-hidden={!isOpen}
				role="menu"
				aria-labelledby={id}
				className="absolute"
			/>
		</>
	);
};

export default NavDropdownItemWithChildren;
